import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Download, Eye, RefreshCw, CheckCircle2, Clock, XCircle, AlertTriangle, MoreVertical, FileText, FileDown, User, Star, Settings, Terminal, DollarSign, Activity, Timer, RotateCcw, Cpu, X, ChevronDown, Database, BarChart3, Zap, Play, Trash2, CheckSquare, Square, MinusSquare } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { Tabs } from '../ui/Tabs';
interface AIAllGenerationsManagerProps {
  success: (msg: string) => void;
  setDetailPanel: (panel: any) => void;
}
// --- NAMES ---
const USER_NAMES = ['Анна Петрова', 'Иван Сидоров', 'Maria Schmidt', 'Олег Козлов', 'Елена Волкова', 'Pierre Dupont', 'Дмитрий Новиков', 'Светлана Иванова', 'Алексей Смирнов', 'Ольга Кузнецова', 'Наталья Морозова', 'Сергей Попов', 'Екатерина Лебедева', 'Андрей Козлов', 'Мария Новикова'];
const ASTROLOGER_NAMES = ['Виктория Звёздная', 'Алексей Лунный', 'Ольга Солнцева', null, null, null, null, null];
const REPORT_TYPES = ['Натальная карта', 'Совместимость', 'Прогноз', 'Соляр', 'Комбо'];
const MODELS = ['GPT-5', 'Claude 3.5', 'Llama 3'];
const SOURCES: Array<'user' | 'astrologer' | 'system'> = ['user', 'user', 'user', 'astrologer', 'system'];
const STATUSES = ['completed', 'completed', 'completed', 'completed', 'processing', 'queued', 'failed', 'canceled', 'reviewing'];
const ERRORS = ['API Timeout exceeded 120s', 'Rate limit exceeded', 'Token overflow (max 8192)', 'Model unavailable', null, null, null, null];
function randomFrom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
// --- GENERATE MOCK DATA ---
const MOCK_DATA = Array.from({
  length: 87
}, (_, i) => {
  const status = randomFrom(STATUSES);
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';
  const source = randomFrom(SOURCES);
  const astrologer = source === 'astrologer' ? randomFrom(ASTROLOGER_NAMES.filter(Boolean)) : randomFrom(ASTROLOGER_NAMES);
  const retries = isFailed ? Math.floor(Math.random() * 3) + 1 : status === 'completed' && Math.random() > 0.8 ? 1 : 0;
  const tokens = isCompleted ? Math.floor(Math.random() * 7000) + 2000 : isFailed ? Math.floor(Math.random() * 1500) : 0;
  const cost = isCompleted ? parseFloat((Math.random() * 0.4 + 0.05).toFixed(2)) : isFailed ? parseFloat((Math.random() * 0.1).toFixed(2)) : 0;
  const duration = isCompleted ? `${Math.floor(Math.random() * 90) + 15} сек` : status === 'processing' ? '—' : '—';
  const hasPdf = isCompleted && Math.random() > 0.05;
  const qualityRand = Math.random();
  const quality = isCompleted ? qualityRand > 0.85 ? 'complaint' : qualityRand > 0.1 ? 'ok' : 'review' : null;
  const type = randomFrom(REPORT_TYPES);
  const createdDate = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000);
  const updatedDate = new Date(createdDate.getTime() + Math.random() * 300000);
  return {
    id: `GEN-${4200 + i}`,
    userId: `USR-${1000 + Math.floor(Math.random() * 500)}`,
    userName: randomFrom(USER_NAMES),
    astrologerId: astrologer ? `AST-${200 + Math.floor(Math.random() * 50)}` : null,
    astrologerName: astrologer,
    orderId: `ORD-${1200 + i}`,
    templateId: `TPL-0${Math.floor(Math.random() * 5) + 1}`,
    type,
    status,
    model: randomFrom(MODELS),
    source,
    priority: Math.random() > 0.8 ? 'high' : Math.random() > 0.3 ? 'medium' : 'low',
    cost,
    tokens,
    duration,
    retries,
    maxRetries: 3,
    hasPdf,
    pdfUrl: hasPdf ? `/reports/GEN-${4200 + i}.pdf` : null,
    pdfStatus: hasPdf ? 'ready' : isCompleted ? 'generating' : 'pending',
    quality,
    error: isFailed ? randomFrom(ERRORS.filter(Boolean)) : null,
    createdAt: createdDate.toISOString(),
    updatedAt: updatedDate.toISOString(),
    createdAtFormatted: createdDate.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    userInput: {
      birthDate: randomFrom(['1990-03-15', '1985-07-22', '1998-11-08', '2001-01-30', '1976-06-12', '1993-09-04', '1988-12-19']),
      birthTime: randomFrom(['14:30', '08:15', '23:45', '06:00', '17:20', '11:05', '02:40']),
      birthPlace: randomFrom(['Москва, Россия', 'Санкт-Петербург, Россия', 'Киев, Украина', 'Берлин, Германия', 'Париж, Франция', 'Новосибирск, Россия', 'Минск, Беларусь']),
      partnerBirthDate: type === 'Совместимость' ? randomFrom(['1992-05-10', '1988-12-01', '1995-09-25', '1991-04-17']) : null,
      partnerBirthTime: type === 'Совместимость' ? randomFrom(['10:00', '19:30', '03:15', '15:45']) : null,
      partnerBirthPlace: type === 'Совместимость' ? randomFrom(['Казань, Россия', 'Минск, Беларусь', 'Варшава, Польша', 'Рига, Латвия']) : null,
      language: 'ru',
      style: randomFrom(['detailed', 'brief', 'poetic'])
    }
  };
}).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
const STATUS_CONFIG: Record<string, {
  label: string;
  variant: any;
}> = {
  completed: {
    label: 'Готово',
    variant: 'success'
  },
  processing: {
    label: 'Генерация',
    variant: 'warning'
  },
  queued: {
    label: 'В очереди',
    variant: 'neutral'
  },
  failed: {
    label: 'Ошибка',
    variant: 'error'
  },
  canceled: {
    label: 'Отменено',
    variant: 'neutral'
  },
  reviewing: {
    label: 'На проверке',
    variant: 'pro'
  }
};
const SOURCE_CONFIG: Record<string, {
  label: string;
  icon: any;
  color: string;
}> = {
  user: {
    label: 'Пользователь',
    icon: User,
    color: 'text-slate-500'
  },
  astrologer: {
    label: 'Астролог',
    icon: Star,
    color: 'text-amber-600'
  },
  system: {
    label: 'Система',
    icon: Settings,
    color: 'text-indigo-500'
  }
};
const QUALITY_CONFIG: Record<string, {
  label: string;
  variant: any;
}> = {
  ok: {
    label: 'OK',
    variant: 'success'
  },
  review: {
    label: 'Review',
    variant: 'warning'
  },
  complaint: {
    label: 'Жалоба',
    variant: 'error'
  }
};
export function AIAllGenerationsManager({
  success,
  setDetailPanel
}: AIAllGenerationsManagerProps) {
  const [data, setData] = useState(MOCK_DATA);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    model: 'all',
    source: 'all',
    hasPdf: 'all',
    quality: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [detailTab, setDetailTab] = useState('main');
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  useEffect(() => {
    const handleClick = () => setOpenMenuId(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
  // Stats
  const stats = useMemo(() => ({
    total: data.length,
    completed: data.filter(d => d.status === 'completed').length,
    failed: data.filter(d => d.status === 'failed').length,
    processing: data.filter(d => d.status === 'processing').length,
    queued: data.filter(d => d.status === 'queued').length,
    avgCost: (data.filter(d => d.cost > 0).reduce((s, d) => s + d.cost, 0) / Math.max(data.filter(d => d.cost > 0).length, 1)).toFixed(2),
    avgTime: '48 сек'
  }), [data]);
  // Filter + search
  const filtered = useMemo(() => {
    return data.filter(item => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!item.id.toLowerCase().includes(q) && !item.userName.toLowerCase().includes(q) && !(item.astrologerName || '').toLowerCase().includes(q)) return false;
      }
      if (filters.status !== 'all' && item.status !== filters.status) return false;
      if (filters.type !== 'all' && item.type !== filters.type) return false;
      if (filters.model !== 'all' && item.model !== filters.model) return false;
      if (filters.source !== 'all' && item.source !== filters.source) return false;
      if (filters.hasPdf !== 'all') {
        if (filters.hasPdf === 'yes' && !item.hasPdf) return false;
        if (filters.hasPdf === 'no' && item.hasPdf) return false;
      }
      if (filters.quality !== 'all' && item.quality !== filters.quality) return false;
      return true;
    });
  }, [data, searchQuery, filters]);
  // Sort
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = (a as any)[sortField];
      const bv = (b as any)[sortField];
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });
  }, [filtered, sortField, sortDir]);
  const paginated = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(sorted.length / itemsPerPage);
  // Selection
  const allOnPageSelected = paginated.length > 0 && paginated.every(p => selectedIds.has(p.id));
  const someOnPageSelected = paginated.some(p => selectedIds.has(p.id));
  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      const newSet = new Set(selectedIds);
      paginated.forEach(p => newSet.delete(p.id));
      setSelectedIds(newSet);
    } else {
      const newSet = new Set(selectedIds);
      paginated.forEach(p => newSet.add(p.id));
      setSelectedIds(newSet);
    }
  };
  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setSelectedIds(newSet);
  };
  // Actions
  const handleRowAction = (action: string, item: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setOpenMenuId(null);
    switch (action) {
      case 'view':
        setSelectedItem(item);
        setDetailTab('main');
        setShowDetailModal(true);
        break;
      case 'pdf':
        success(`Скачивание PDF для ${item.id}...`);
        break;
      case 'retry':
        setData(prev => prev.map(d => d.id === item.id ? {
          ...d,
          status: 'queued',
          retries: d.retries + 1
        } : d));
        success(`Генерация ${item.id} отправлена на повтор`);
        break;
      case 'cancel':
        setData(prev => prev.map(d => d.id === item.id ? {
          ...d,
          status: 'canceled'
        } : d));
        success(`Генерация ${item.id} отменена`);
        break;
      case 'logs':
        setSelectedItem(item);
        setDetailTab('logs');
        setShowDetailModal(true);
        break;
    }
  };
  const handleBatchAction = (action: string) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    switch (action) {
      case 'retry':
        setData(prev => prev.map(d => ids.includes(d.id) && ['failed', 'canceled'].includes(d.status) ? {
          ...d,
          status: 'queued',
          retries: d.retries + 1
        } : d));
        success(`${ids.length} генераций отправлены на повтор`);
        break;
      case 'cancel':
        setData(prev => prev.map(d => ids.includes(d.id) && ['queued', 'processing'].includes(d.status) ? {
          ...d,
          status: 'canceled'
        } : d));
        success(`${ids.length} генераций отменены`);
        break;
      case 'export':
        success(`Экспорт ${ids.length} записей в CSV...`);
        break;
    }
    setSelectedIds(new Set());
  };
  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');else {
      setSortField(field);
      setSortDir('desc');
    }
  };
  const resetFilters = () => {
    setFilters({
      status: 'all',
      type: 'all',
      model: 'all',
      source: 'all',
      hasPdf: 'all',
      quality: 'all'
    });
    setSearchQuery('');
    setCurrentPage(1);
  };
  const SortHeader = ({
    field,
    children
  }: {
    field: string;
    children: React.ReactNode;
  }) => <th className="px-3 py-3 font-medium cursor-pointer hover:text-indigo-600 select-none" onClick={() => handleSort(field)} data-id="element-8011">
      <span className="flex items-center gap-1" data-id="element-8012">
        {children}
        {sortField === field && <ChevronDown className={`w-3 h-3 transition-transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} data-id="element-8013" />}
      </span>
    </th>;
  // Detail Modal
  const renderDetailModal = () => {
    if (!selectedItem) return null;
    return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={() => setShowDetailModal(false)} data-id="element-8014">
        <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={(e: React.MouseEvent) => e.stopPropagation()} data-id="element-8015">
          <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-white shrink-0" data-id="element-8016">
            <div data-id="element-8017">
              <div className="flex items-center gap-3 mb-1" data-id="element-8018">
                <h2 className="text-lg font-bold text-slate-900" data-id="element-8019">
                  {selectedItem.id}
                </h2>
                <Badge variant={STATUS_CONFIG[selectedItem.status]?.variant} data-id="element-8020">
                  {STATUS_CONFIG[selectedItem.status]?.label}
                </Badge>
                {selectedItem.quality && <Badge variant={QUALITY_CONFIG[selectedItem.quality]?.variant} size="sm" data-id="element-8021">
                    {QUALITY_CONFIG[selectedItem.quality]?.label}
                  </Badge>}
              </div>
              <p className="text-sm text-slate-500" data-id="element-8022">
                {selectedItem.type} • {selectedItem.createdAtFormatted} • Заказ:{' '}
                {selectedItem.orderId}
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-8023">
              {selectedItem.hasPdf && <Button variant="outline" size="sm" icon={<FileDown className="w-4 h-4" data-id="element-8025" />} onClick={() => handleRowAction('pdf', selectedItem)} data-id="element-8024">
                  PDF
                </Button>}
              <button onClick={() => setShowDetailModal(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100" data-id="element-8026">
                <X className="w-5 h-5" data-id="element-8027" />
              </button>
            </div>
          </div>
          <div className="px-6 border-b border-slate-100 bg-white shrink-0" data-id="element-8028">
            <Tabs activeTab={detailTab} onChange={setDetailTab} tabs={[{
            id: 'main',
            label: 'Основное'
          }, {
            id: 'content',
            label: 'Контент'
          }, {
            id: 'pdf',
            label: 'PDF'
          }, {
            id: 'prompt',
            label: 'Prompt'
          }, {
            id: 'cost',
            label: 'Стоимость'
          }, {
            id: 'logs',
            label: 'Логи'
          }, {
            id: 'errors',
            label: 'Ошибки'
          }]} data-id="element-8029" />
          </div>
          <div className="p-6 overflow-y-auto bg-slate-50 flex-1" data-id="element-8030">
            {detailTab === 'main' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8031">
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8032">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8033">
                    Участники
                  </h3>
                  <div className="space-y-3" data-id="element-8034">
                    {[['Пользователь', `${selectedItem.userName} (${selectedItem.userId})`], ['Астролог', selectedItem.astrologerName ? `${selectedItem.astrologerName} (${selectedItem.astrologerId})` : '—'], ['Источник', SOURCE_CONFIG[selectedItem.source]?.label], ['Заказ', selectedItem.orderId], ['Шаблон', selectedItem.templateId]].map(([label, value]) => <div key={label as string} className="flex justify-between py-2 border-b border-slate-100" data-id="element-8035">
                        <span className="text-sm text-slate-500" data-id="element-8036">{label}</span>
                        <span className="text-sm font-medium text-slate-900" data-id="element-8037">
                          {value}
                        </span>
                      </div>)}
                  </div>
                </Card>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8038">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8039">
                    Данные клиента
                  </h3>
                  <div className="space-y-3" data-id="element-8040">
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8041">
                      <span className="text-sm text-slate-500" data-id="element-8042">
                        Дата рождения
                      </span>
                      <span className="text-sm font-medium text-slate-900" data-id="element-8043">
                        {selectedItem.userInput?.birthDate || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8044">
                      <span className="text-sm text-slate-500" data-id="element-8045">
                        Время рождения
                      </span>
                      <span className="text-sm font-medium text-slate-900" data-id="element-8046">
                        {selectedItem.userInput?.birthTime || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8047">
                      <span className="text-sm text-slate-500" data-id="element-8048">
                        Место рождения
                      </span>
                      <span className="text-sm font-medium text-slate-900" data-id="element-8049">
                        {selectedItem.userInput?.birthPlace || '—'}
                      </span>
                    </div>
                    {selectedItem.type === 'Совместимость' && selectedItem.userInput?.partnerBirthDate && <>
                          <div className="pt-2 pb-1" data-id="element-8050">
                            <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider" data-id="element-8051">
                              Партнёр
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8052">
                            <span className="text-sm text-slate-500" data-id="element-8053">
                              Дата рождения
                            </span>
                            <span className="text-sm font-medium text-slate-900" data-id="element-8054">
                              {selectedItem.userInput.partnerBirthDate}
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8055">
                            <span className="text-sm text-slate-500" data-id="element-8056">
                              Время рождения
                            </span>
                            <span className="text-sm font-medium text-slate-900" data-id="element-8057">
                              {selectedItem.userInput.partnerBirthTime || '—'}
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8058">
                            <span className="text-sm text-slate-500" data-id="element-8059">
                              Место рождения
                            </span>
                            <span className="text-sm font-medium text-slate-900" data-id="element-8060">
                              {selectedItem.userInput.partnerBirthPlace || '—'}
                            </span>
                          </div>
                        </>}
                  </div>
                </Card>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8061">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8062">
                    Генерация
                  </h3>
                  <div className="space-y-3" data-id="element-8063">
                    {[['Статус', STATUS_CONFIG[selectedItem.status]?.label], ['Модель', selectedItem.model], ['Время генерации', selectedItem.duration], ['Попытки', `${selectedItem.retries}/${selectedItem.maxRetries}`], ['Приоритет', selectedItem.priority], ['Создан', selectedItem.createdAtFormatted]].map(([label, value]) => <div key={label as string} className="flex justify-between py-2 border-b border-slate-100" data-id="element-8064">
                        <span className="text-sm text-slate-500" data-id="element-8065">{label}</span>
                        <span className="text-sm font-medium text-slate-900" data-id="element-8066">
                          {value}
                        </span>
                      </div>)}
                  </div>
                </Card>
              </div>}
            {detailTab === 'content' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8067">
                {selectedItem.status === 'completed' ? <div className="p-4 bg-slate-100 rounded-lg font-mono text-xs text-slate-700 whitespace-pre-wrap max-h-[400px] overflow-y-auto" data-id="element-8068">
                    {`{
  "reportId": "${selectedItem.id}",
  "type": "${selectedItem.type}",
  "language": "ru",
  "sections": [
    {
      "heading": "Введение",
      "content": "Ваша натальная карта показывает сильное влияние стихии воды..."
    },
    {
      "heading": "Личность и характер",
      "content": "Солнце в Скорпионе наделяет вас глубокой интуицией..."
    },
    {
      "heading": "Кармические задачи",
      "content": "Северный узел в Стрельце указывает на необходимость..."
    }
  ]
}`}
                  </div> : <div className="text-center py-12 text-slate-500" data-id="element-8069">
                    <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" data-id="element-8070" />
                    <p data-id="element-8071">
                      Контент недоступен. Статус:{' '}
                      {STATUS_CONFIG[selectedItem.status]?.label}
                    </p>
                  </div>}
              </Card>}
            {detailTab === 'pdf' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8072">
                {selectedItem.hasPdf ? <div className="text-center py-8" data-id="element-8073">
                    <div className="w-20 h-20 mx-auto mb-4 bg-rose-50 rounded-2xl flex items-center justify-center" data-id="element-8074">
                      <FileText className="w-10 h-10 text-rose-500" data-id="element-8075" />
                    </div>
                    <h4 className="font-bold text-slate-900 mb-1" data-id="element-8076">
                      {selectedItem.id}.pdf
                    </h4>
                    <p className="text-sm text-slate-500 mb-6" data-id="element-8077">
                      PDF отчёт готов к скачиванию
                    </p>
                    <div className="flex justify-center gap-3" data-id="element-8078">
                      <Button variant="primary" icon={<FileDown className="w-4 h-4" data-id="element-8080" />} onClick={() => success('Скачивание PDF...')} data-id="element-8079">
                        Скачать PDF
                      </Button>
                      <Button variant="outline" icon={<Eye className="w-4 h-4" data-id="element-8082" />} onClick={() => success('Открытие PDF...')} data-id="element-8081">
                        Просмотр
                      </Button>
                    </div>
                    <div className="mt-6 text-xs text-slate-400" data-id="element-8083">
                      Доступен: пользователю •{' '}
                      {selectedItem.astrologerName ? 'астрологу • ' : ''}
                      администратору
                    </div>
                  </div> : <div className="text-center py-12 text-slate-500" data-id="element-8084">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" data-id="element-8085" />
                    <p data-id="element-8086">
                      PDF{' '}
                      {selectedItem.pdfStatus === 'generating' ? 'генерируется...' : 'ещё не создан'}
                    </p>
                  </div>}
              </Card>}
            {detailTab === 'prompt' && <div className="space-y-6" data-id="element-8087">
                {/* User Input Data */}
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8088">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4" data-id="element-8089">
                    Данные пользователя (входные)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8090">
                    <div className="space-y-3" data-id="element-8091">
                      <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8092">
                        <span className="text-sm text-slate-500" data-id="element-8093">
                          Дата рождения
                        </span>
                        <span className="text-sm font-medium text-slate-900" data-id="element-8094">
                          {selectedItem.userInput?.birthDate || '—'}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8095">
                        <span className="text-sm text-slate-500" data-id="element-8096">
                          Время рождения
                        </span>
                        <span className="text-sm font-medium text-slate-900" data-id="element-8097">
                          {selectedItem.userInput?.birthTime || '—'}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8098">
                        <span className="text-sm text-slate-500" data-id="element-8099">
                          Место рождения
                        </span>
                        <span className="text-sm font-medium text-slate-900" data-id="element-8100">
                          {selectedItem.userInput?.birthPlace || '—'}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8101">
                        <span className="text-sm text-slate-500" data-id="element-8102">Язык</span>
                        <span className="text-sm font-medium text-slate-900" data-id="element-8103">
                          {selectedItem.userInput?.language === 'ru' ? 'Русский' : selectedItem.userInput?.language || '—'}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8104">
                        <span className="text-sm text-slate-500" data-id="element-8105">
                          Стиль отчёта
                        </span>
                        <span className="text-sm font-medium text-slate-900 capitalize" data-id="element-8106">
                          {selectedItem.userInput?.style || '—'}
                        </span>
                      </div>
                    </div>
                    {selectedItem.type === 'Совместимость' && selectedItem.userInput?.partnerBirthDate && <div className="space-y-3" data-id="element-8107">
                          <div className="text-xs font-bold text-indigo-500 uppercase tracking-wider mb-1" data-id="element-8108">
                            Партнёр
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8109">
                            <span className="text-sm text-slate-500" data-id="element-8110">
                              Дата рождения
                            </span>
                            <span className="text-sm font-medium text-slate-900" data-id="element-8111">
                              {selectedItem.userInput.partnerBirthDate}
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8112">
                            <span className="text-sm text-slate-500" data-id="element-8113">
                              Время рождения
                            </span>
                            <span className="text-sm font-medium text-slate-900" data-id="element-8114">
                              {selectedItem.userInput.partnerBirthTime || '—'}
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8115">
                            <span className="text-sm text-slate-500" data-id="element-8116">
                              Место рождения
                            </span>
                            <span className="text-sm font-medium text-slate-900" data-id="element-8117">
                              {selectedItem.userInput.partnerBirthPlace || '—'}
                            </span>
                          </div>
                        </div>}
                  </div>
                </Card>

                {/* Generated Prompt */}
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8118">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8119">
                    Сгенерированный промпт
                  </h4>
                  <div className="p-4 bg-slate-900 text-emerald-400 rounded-lg font-mono text-xs whitespace-pre-wrap mb-4 max-h-[300px] overflow-y-auto" data-id="element-8120">
                    {`You are an expert astrologer with 20+ years of experience.
Generate a comprehensive "${selectedItem.type}" report in Russian.

User Data:
- Birth date: ${selectedItem.userInput?.birthDate || 'N/A'}
- Birth time: ${selectedItem.userInput?.birthTime || 'N/A'}
- Birth place: ${selectedItem.userInput?.birthPlace || 'N/A'}${selectedItem.type === 'Совместимость' && selectedItem.userInput?.partnerBirthDate ? `

Partner Data:
- Birth date: ${selectedItem.userInput.partnerBirthDate}
- Birth time: ${selectedItem.userInput.partnerBirthTime || 'N/A'}
- Birth place: ${selectedItem.userInput.partnerBirthPlace || 'N/A'}` : ''}

Focus on:
- Psychological depth and practical advice
- Specific dates and periods
- Actionable recommendations

Style: ${selectedItem.userInput?.style || 'detailed'}
Language: ${selectedItem.userInput?.language || 'ru'}
Template: ${selectedItem.templateId} (v3.2)
Model: ${selectedItem.model}
Max tokens: 8192
Temperature: 0.7`}
                  </div>
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8121">
                    Версия шаблона
                  </h4>
                  <div className="flex items-center gap-3" data-id="element-8122">
                    <Badge variant="outline" data-id="element-8123">{selectedItem.templateId}</Badge>
                    <span className="text-xs text-slate-500" data-id="element-8124">
                      v3.2 • Обновлён 10.03.2026
                    </span>
                  </div>
                </Card>
              </div>}
            {detailTab === 'cost' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8125">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-8126">
                  {[{
                label: 'Токены (вход)',
                value: selectedItem.tokens,
                icon: <Database className="w-4 h-4 text-blue-500" data-id="element-8127" />
              }, {
                label: 'Токены (выход)',
                value: Math.floor(selectedItem.tokens * 1.5),
                icon: <Database className="w-4 h-4 text-emerald-500" data-id="element-8128" />
              }, {
                label: 'Стоимость',
                value: `$${selectedItem.cost}`,
                icon: <DollarSign className="w-4 h-4 text-amber-500" data-id="element-8129" />
              }, {
                label: 'Время',
                value: selectedItem.duration,
                icon: <Timer className="w-4 h-4 text-indigo-500" data-id="element-8130" />
              }].map(item => <div key={item.label} className="p-4 bg-white border border-slate-200 rounded-lg" data-id="element-8131">
                      <div className="flex items-center gap-2 mb-2" data-id="element-8132">
                        {item.icon}
                        <span className="text-xs text-slate-500" data-id="element-8133">
                          {item.label}
                        </span>
                      </div>
                      <div className="text-lg font-bold text-slate-900" data-id="element-8134">
                        {item.value || '—'}
                      </div>
                    </div>)}
                </div>
              </Card>}
            {detailTab === 'logs' && <Card padding="none" className="shadow-sm border-slate-200 overflow-hidden" data-id="element-8135">
                <div className="bg-slate-900 text-emerald-400 font-mono text-xs p-4 min-h-[350px] max-h-[450px] overflow-y-auto" data-id="element-8136">
                  <div className="mb-2 text-slate-500" data-id="element-8137">
                    [{selectedItem.createdAt}] [INFO] Job created. Priority:{' '}
                    {selectedItem.priority}
                  </div>
                  <div className="mb-2 text-slate-500" data-id="element-8138">
                    [{selectedItem.createdAt}] [INFO] Added to queue. Position:
                    #{Math.floor(Math.random() * 20) + 1}
                  </div>
                  {['processing', 'completed', 'failed', 'reviewing'].includes(selectedItem.status) && <>
                      <div className="mb-2 text-blue-400" data-id="element-8139">
                        [{selectedItem.updatedAt}] [INFO] Worker assigned.
                        Model: {selectedItem.model}
                      </div>
                      <div className="mb-2 text-blue-400" data-id="element-8140">
                        [{selectedItem.updatedAt}] [INFO] Prompt compiled.
                        Template: {selectedItem.templateId}
                      </div>
                      <div className="mb-2 text-blue-400" data-id="element-8141">
                        [{selectedItem.updatedAt}] [INFO] API call started...
                      </div>
                    </>}
                  {selectedItem.status === 'completed' && <>
                      <div className="mb-2 text-emerald-400" data-id="element-8142">
                        [{selectedItem.updatedAt}] [SUCCESS] Generation
                        completed. Tokens: {selectedItem.tokens} in /{' '}
                        {Math.floor(selectedItem.tokens * 1.5)} out
                      </div>
                      <div className="mb-2 text-emerald-400" data-id="element-8143">
                        [{selectedItem.updatedAt}] [SUCCESS] Quality check
                        passed. Score: 4.{Math.floor(Math.random() * 5) + 5}
                      </div>
                      {selectedItem.hasPdf && <div className="mb-2 text-emerald-400" data-id="element-8144">
                          [{selectedItem.updatedAt}] [SUCCESS] PDF generated →
                          S3
                        </div>}
                    </>}
                  {selectedItem.status === 'failed' && <div className="mb-2 text-rose-400" data-id="element-8145">
                      [{selectedItem.updatedAt}] [ERROR] {selectedItem.error}
                    </div>}
                </div>
              </Card>}
            {detailTab === 'errors' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8146">
                {selectedItem.error ? <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-start gap-3" data-id="element-8147">
                    <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" data-id="element-8148" />
                    <div data-id="element-8149">
                      <h4 className="font-bold text-rose-900 mb-1" data-id="element-8150">
                        Ошибка генерации
                      </h4>
                      <p className="text-sm text-rose-700 mb-1" data-id="element-8151">
                        {selectedItem.error}
                      </p>
                      <p className="text-xs text-rose-500 mb-4" data-id="element-8152">
                        Попытка {selectedItem.retries}/{selectedItem.maxRetries}{' '}
                        • Модель: {selectedItem.model}
                      </p>
                      <div className="flex gap-2" data-id="element-8153">
                        <Button variant="primary" size="sm" onClick={() => {
                    handleRowAction('retry', selectedItem);
                    setShowDetailModal(false);
                  }} data-id="element-8154">
                          Повторить
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => {
                    handleRowAction('cancel', selectedItem);
                    setShowDetailModal(false);
                  }} data-id="element-8155">
                          Отменить
                        </Button>
                      </div>
                    </div>
                  </div> : <div className="text-center py-12 text-slate-500" data-id="element-8156">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-400" data-id="element-8157" />
                    <p data-id="element-8158">Ошибок не обнаружено</p>
                  </div>}
              </Card>}
          </div>
        </Card>
      </div>;
  };
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-8159">
      {/* 1. SUMMARY STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-id="element-8160">
        {[{
        label: 'Всего',
        value: stats.total,
        color: 'text-slate-900',
        bg: 'bg-white border-slate-200',
        filter: {
          status: 'all'
        }
      }, {
        label: 'Готово',
        value: stats.completed,
        color: 'text-emerald-700',
        bg: 'bg-emerald-50 border-emerald-100',
        filter: {
          status: 'completed'
        }
      }, {
        label: 'Ошибки',
        value: stats.failed,
        color: 'text-rose-700',
        bg: 'bg-rose-50 border-rose-100',
        filter: {
          status: 'failed'
        }
      }, {
        label: 'Генерация',
        value: stats.processing,
        color: 'text-blue-700',
        bg: 'bg-blue-50 border-blue-100',
        filter: {
          status: 'processing'
        }
      }, {
        label: 'В очереди',
        value: stats.queued,
        color: 'text-amber-700',
        bg: 'bg-amber-50 border-amber-100',
        filter: {
          status: 'queued'
        }
      }, {
        label: 'Ср. стоимость',
        value: `$${stats.avgCost}`,
        color: 'text-slate-700',
        bg: 'bg-slate-50 border-slate-200',
        filter: null
      }, {
        label: 'Ср. время',
        value: stats.avgTime,
        color: 'text-slate-700',
        bg: 'bg-slate-50 border-slate-200',
        filter: null
      }].map(stat => <Card key={stat.label} padding="sm" className={`${stat.bg} shadow-sm flex flex-col justify-center ${stat.filter ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} onClick={stat.filter ? () => {
        setFilters({
          ...filters,
          ...stat.filter
        });
        setCurrentPage(1);
      } : undefined} data-id="element-8161">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1" data-id="element-8162">
              {stat.label}
            </div>
            <div className={`text-xl font-bold ${stat.color}`} data-id="element-8163">
              {stat.value}
            </div>
          </Card>)}
      </div>

      {/* 2. SEARCH + FILTERS + BATCH ACTIONS */}
      <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8164">
        <div className="flex flex-col lg:flex-row justify-between gap-4" data-id="element-8165">
          <div className="flex flex-wrap items-center gap-2" data-id="element-8166">
            {selectedIds.size > 0 && <>
                <Badge variant="pro" size="sm" data-id="element-8167">
                  {selectedIds.size} выбрано
                </Badge>
                <Button variant="outline" size="sm" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-8169" />} onClick={() => handleBatchAction('retry')} data-id="element-8168">
                  Повторить
                </Button>
                <Button variant="outline" size="sm" icon={<XCircle className="w-3.5 h-3.5" data-id="element-8171" />} onClick={() => handleBatchAction('cancel')} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-id="element-8170">
                  Отменить
                </Button>
                <Button variant="outline" size="sm" icon={<Download className="w-3.5 h-3.5" data-id="element-8173" />} onClick={() => handleBatchAction('export')} data-id="element-8172">
                  Экспорт
                </Button>
                <div className="w-px h-5 bg-slate-200" data-id="element-8174" />
              </>}
            <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-8176" />} onClick={() => success('Экспорт CSV всех записей')} data-id="element-8175">
              CSV
            </Button>
          </div>
          <div className="flex items-center gap-2 w-full lg:w-auto" data-id="element-8177">
            <div className="relative flex-1 lg:w-72" data-id="element-8178">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-8179" />
              <input type="text" placeholder="Поиск по ID, имени, астрологу..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" value={searchQuery} onChange={e => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }} data-id="element-8180" />
            </div>
            <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Search className="w-4 h-4" data-id="element-8182" />} onClick={() => setShowFilters(!showFilters)} data-id="element-8181">
              Фильтры
            </Button>
          </div>
        </div>

        {showFilters && <div className="mt-4 pt-4 border-t border-slate-100 animate-in slide-in-from-top-2" data-id="element-8183">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4" data-id="element-8184">
              <div data-id="element-8185">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-8186">
                  Статус
                </label>
                <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" value={filters.status} onChange={e => {
              setFilters({
                ...filters,
                status: e.target.value
              });
              setCurrentPage(1);
            }} data-id="element-8187">
                  <option value="all" data-id="element-8188">Все</option>
                  <option value="completed" data-id="element-8189">Готово</option>
                  <option value="processing" data-id="element-8190">Генерация</option>
                  <option value="queued" data-id="element-8191">В очереди</option>
                  <option value="failed" data-id="element-8192">Ошибка</option>
                  <option value="canceled" data-id="element-8193">Отменено</option>
                  <option value="reviewing" data-id="element-8194">На проверке</option>
                </select>
              </div>
              <div data-id="element-8195">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-8196">
                  Тип
                </label>
                <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" value={filters.type} onChange={e => {
              setFilters({
                ...filters,
                type: e.target.value
              });
              setCurrentPage(1);
            }} data-id="element-8197">
                  <option value="all" data-id="element-8198">Все</option>
                  {REPORT_TYPES.map(t => <option key={t} value={t} data-id="element-8199">
                      {t}
                    </option>)}
                </select>
              </div>
              <div data-id="element-8200">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-8201">
                  Модель
                </label>
                <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" value={filters.model} onChange={e => {
              setFilters({
                ...filters,
                model: e.target.value
              });
              setCurrentPage(1);
            }} data-id="element-8202">
                  <option value="all" data-id="element-8203">Все</option>
                  {MODELS.map(m => <option key={m} value={m} data-id="element-8204">
                      {m}
                    </option>)}
                </select>
              </div>
              <div data-id="element-8205">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-8206">
                  Источник
                </label>
                <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" value={filters.source} onChange={e => {
              setFilters({
                ...filters,
                source: e.target.value
              });
              setCurrentPage(1);
            }} data-id="element-8207">
                  <option value="all" data-id="element-8208">Все</option>
                  <option value="user" data-id="element-8209">Пользователь</option>
                  <option value="astrologer" data-id="element-8210">Астролог</option>
                  <option value="system" data-id="element-8211">Система</option>
                </select>
              </div>
              <div data-id="element-8212">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-8213">
                  PDF
                </label>
                <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" value={filters.hasPdf} onChange={e => {
              setFilters({
                ...filters,
                hasPdf: e.target.value
              });
              setCurrentPage(1);
            }} data-id="element-8214">
                  <option value="all" data-id="element-8215">Все</option>
                  <option value="yes" data-id="element-8216">Есть PDF</option>
                  <option value="no" data-id="element-8217">Нет PDF</option>
                </select>
              </div>
              <div data-id="element-8218">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-8219">
                  Качество
                </label>
                <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" value={filters.quality} onChange={e => {
              setFilters({
                ...filters,
                quality: e.target.value
              });
              setCurrentPage(1);
            }} data-id="element-8220">
                  <option value="all" data-id="element-8221">Все</option>
                  <option value="ok" data-id="element-8222">OK</option>
                  <option value="review" data-id="element-8223">На проверке</option>
                  <option value="complaint" data-id="element-8224">Жалоба</option>
                </select>
              </div>
            </div>
            <div className="mt-3 flex justify-end" data-id="element-8225">
              <Button variant="ghost" size="sm" onClick={resetFilters} data-id="element-8226">
                Сбросить фильтры
              </Button>
            </div>
          </div>}
      </Card>

      {/* 3. TABLE */}
      <Card padding="none" className="shadow-sm border-slate-200 overflow-visible" data-id="element-8227">
        <div className="overflow-x-auto" data-id="element-8228">
          <table className="w-full text-left whitespace-nowrap" data-id="element-8229">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider" data-id="element-8230">
              <tr data-id="element-8231">
                <th className="px-3 py-3 w-10" data-id="element-8232">
                  <button onClick={toggleSelectAll} className="text-slate-400 hover:text-slate-600" data-id="element-8233">
                    {allOnPageSelected ? <CheckSquare className="w-4 h-4 text-indigo-600" data-id="element-8234" /> : someOnPageSelected ? <MinusSquare className="w-4 h-4 text-indigo-400" data-id="element-8235" /> : <Square className="w-4 h-4" data-id="element-8236" />}
                  </button>
                </th>
                <SortHeader field="id" data-id="element-8237">ID / Дата</SortHeader>
                <SortHeader field="userName" data-id="element-8238">Пользователь</SortHeader>
                <th className="px-3 py-3 font-medium" data-id="element-8239">Астролог</th>
                <SortHeader field="type" data-id="element-8240">Тип</SortHeader>
                <SortHeader field="status" data-id="element-8241">Статус</SortHeader>
                <SortHeader field="duration" data-id="element-8242">Время</SortHeader>
                <th className="px-3 py-3 font-medium" data-id="element-8243">Модель</th>
                <SortHeader field="cost" data-id="element-8244">Стоимость</SortHeader>
                <th className="px-3 py-3 font-medium" data-id="element-8245">Токены</th>
                <th className="px-3 py-3 font-medium" data-id="element-8246">PDF</th>
                <th className="px-3 py-3 font-medium" data-id="element-8247">Источник</th>
                <th className="px-3 py-3 font-medium text-right" data-id="element-8248">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-8249">
              {paginated.map(g => {
              const SourceIcon = SOURCE_CONFIG[g.source]?.icon || User;
              const isSelected = selectedIds.has(g.id);
              return <tr key={g.id} className={`transition-colors ${isSelected ? 'bg-indigo-50/50' : 'hover:bg-slate-50'} ${g.status === 'failed' ? 'border-l-2 border-l-rose-400' : ''}`} data-id="element-8250">
                    <td className="px-3 py-3" data-id="element-8251">
                      <button onClick={() => toggleSelect(g.id)} className="text-slate-400 hover:text-slate-600" data-id="element-8252">
                        {isSelected ? <CheckSquare className="w-4 h-4 text-indigo-600" data-id="element-8253" /> : <Square className="w-4 h-4" data-id="element-8254" />}
                      </button>
                    </td>
                    <td className="px-3 py-3" data-id="element-8255">
                      <span className="text-xs font-bold text-indigo-600 cursor-pointer hover:underline" onClick={() => handleRowAction('view', g)} data-id="element-8256">
                        {g.id}
                      </span>
                      <div className="text-[9px] text-slate-400 mt-0.5" data-id="element-8257">
                        {g.createdAtFormatted}
                      </div>
                    </td>
                    <td className="px-3 py-3" data-id="element-8258">
                      <span className="text-sm text-slate-900 cursor-pointer hover:text-indigo-600" data-id="element-8259">
                        {g.userName}
                      </span>
                      <div className="text-[9px] text-slate-400" data-id="element-8260">
                        {g.userId}
                      </div>
                    </td>
                    <td className="px-3 py-3" data-id="element-8261">
                      {g.astrologerName ? <span className="text-xs text-amber-700 cursor-pointer hover:underline flex items-center gap-1" data-id="element-8262">
                          <Star className="w-3 h-3" data-id="element-8263" />
                          {g.astrologerName}
                        </span> : <span className="text-xs text-slate-300" data-id="element-8264">—</span>}
                    </td>
                    <td className="px-3 py-3" data-id="element-8265">
                      <span className="text-xs text-slate-700 cursor-pointer hover:text-indigo-600" data-id="element-8266">
                        {g.type}
                      </span>
                    </td>
                    <td className="px-3 py-3" data-id="element-8267">
                      <div className="flex flex-col gap-1 items-start" data-id="element-8268">
                        <Badge variant={STATUS_CONFIG[g.status]?.variant} size="sm" className="text-[10px] cursor-pointer" onClick={() => {
                      setFilters({
                        ...filters,
                        status: g.status
                      });
                      setCurrentPage(1);
                    }} data-id="element-8269">
                          {STATUS_CONFIG[g.status]?.label}
                        </Badge>
                        {g.error && <span className="text-[9px] text-rose-500 truncate max-w-[120px]" title={g.error} data-id="element-8270">
                            {g.error}
                          </span>}
                        {g.quality && g.quality !== 'ok' && <Badge variant={QUALITY_CONFIG[g.quality]?.variant} size="sm" className="text-[9px]" data-id="element-8271">
                            {QUALITY_CONFIG[g.quality]?.label}
                          </Badge>}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600" data-id="element-8272">
                      {g.duration}
                    </td>
                    <td className="px-3 py-3" data-id="element-8273">
                      <Badge variant="outline" size="sm" className="text-[9px] bg-white" data-id="element-8274">
                        {g.model}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 text-xs font-medium text-emerald-600" data-id="element-8275">
                      ${g.cost.toFixed(2)}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-500" data-id="element-8276">
                      {g.tokens > 0 ? <span className="flex items-center gap-1" data-id="element-8277">
                          <CheckCircle2 className="w-3 h-3 text-emerald-500" data-id="element-8278" />{' '}
                          {g.tokens.toLocaleString()}
                        </span> : '—'}
                    </td>
                    <td className="px-3 py-3" data-id="element-8279">
                      {g.hasPdf ? <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded cursor-pointer hover:bg-emerald-100" onClick={() => handleRowAction('pdf', g)} data-id="element-8280">
                          <FileDown className="w-3 h-3" data-id="element-8281" /> PDF
                        </span> : g.pdfStatus === 'generating' ? <span className="flex items-center gap-1 text-[10px] text-amber-500" data-id="element-8282">
                          <RefreshCw className="w-3 h-3 animate-spin" data-id="element-8283" /> Ген.
                        </span> : <span className="text-[10px] text-slate-300" data-id="element-8284">—</span>}
                    </td>
                    <td className="px-3 py-3" data-id="element-8285">
                      <span className={`flex items-center gap-1 text-[10px] ${SOURCE_CONFIG[g.source]?.color}`} data-id="element-8286">
                        <SourceIcon className="w-3 h-3" data-id="element-8287" />
                        {g.source === 'astrologer' ? 'Астр.' : g.source === 'system' ? 'Сист.' : 'Польз.'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right relative" data-id="element-8288">
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-slate-900" icon={<MoreVertical className="w-3.5 h-3.5" data-id="element-8290" />} onClick={e => {
                    e.stopPropagation();
                    setOpenMenuId(openMenuId === g.id ? null : g.id);
                  }} data-id="element-8289" />
                      {openMenuId === g.id && <div className="absolute right-8 top-2 w-52 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50 animate-in fade-in zoom-in-95" data-id="element-8291">
                          <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleRowAction('view', g, e)} data-id="element-8292">
                            <Eye className="w-3.5 h-3.5 text-slate-400" data-id="element-8293" />{' '}
                            Открыть отчёт
                          </button>
                          {g.hasPdf && <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleRowAction('pdf', g, e)} data-id="element-8294">
                              <FileDown className="w-3.5 h-3.5 text-emerald-500" data-id="element-8295" />{' '}
                              Скачать PDF
                            </button>}
                          <div className="h-px bg-slate-100 my-1" data-id="element-8296" />
                          {['failed', 'canceled'].includes(g.status) && <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleRowAction('retry', g, e)} data-id="element-8297">
                              <RotateCcw className="w-3.5 h-3.5 text-amber-500" data-id="element-8298" />{' '}
                              Повторить генерацию
                            </button>}
                          {['queued', 'processing'].includes(g.status) && <button className="w-full text-left px-4 py-2 text-xs text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={e => handleRowAction('cancel', g, e)} data-id="element-8299">
                              <XCircle className="w-3.5 h-3.5" data-id="element-8300" /> Отменить
                            </button>}
                          <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleRowAction('logs', g, e)} data-id="element-8301">
                            <Terminal className="w-3.5 h-3.5 text-slate-400" data-id="element-8302" />{' '}
                            Логи / Prompt
                          </button>
                        </div>}
                    </td>
                  </tr>;
            })}
              {paginated.length === 0 && <tr data-id="element-8303">
                  <td colSpan={13} className="px-4 py-12 text-center text-slate-500" data-id="element-8304">
                    <Database className="w-12 h-12 mx-auto mb-4 text-slate-300" data-id="element-8305" />
                    <p className="text-sm font-medium" data-id="element-8306">Ничего не найдено</p>
                    <p className="text-xs mt-1" data-id="element-8307">Попробуйте изменить фильтры</p>
                    <Button variant="ghost" size="sm" className="mt-3" onClick={resetFilters} data-id="element-8308">
                      Сбросить фильтры
                    </Button>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex items-center justify-between" data-id="element-8309">
            <span className="text-xs text-slate-500" data-id="element-8310">
              Показано {(currentPage - 1) * itemsPerPage + 1}–
              {Math.min(currentPage * itemsPerPage, sorted.length)} из{' '}
              {sorted.length}
            </span>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-8311" />
          </div>}
      </Card>

      {showDetailModal && renderDetailModal()}
    </div>;
}